import {useContext} from 'react';
import block from 'bem-cn-lite';
import {Text, Icon, Button} from '@gravity-ui/uikit';
import {Comments, Copy, Envelope} from '@gravity-ui/icons';
import {TooltipCopy} from '@components';
import {AppContext} from '@app/context';
import {ClientDetails} from '@app/interfaces/ClientDetails';
import ManagerPng from '@assets/avatar-manager.png';
import {openTalkMeSupport} from 'widgets';

import './ManagerInfo.scss';

const b = block('manager-info');

type ContactPerson = ClientDetails['mop'];

const hasContactData = (contact?: ContactPerson | null) =>
    Boolean(contact?.name || contact?.phone || contact?.email);

interface ManagerContactBlockProps {
    contact: ContactPerson;
    title: React.ReactNode;
    avatarAlt: string;
    showSocialButton?: boolean;
}

const ManagerContactBlock: React.FC<ManagerContactBlockProps> = ({
    contact,
    title,
    avatarAlt,
    showSocialButton = false
}) => {
    const avatarSrc = contact?.photoUrl || ManagerPng;

    return (
        <div className={b('contact-container')}>
            <img
                src={avatarSrc}
                className={b('avatar')}
                alt={avatarAlt}
                onError={(e) => {
                    e.currentTarget.onerror = null; // защита от бесконечного цикла
                    e.currentTarget.src = ManagerPng;
                }}
            />

            <div className={b('contact-info')}>
                <div className={b('identity')}>
                    <Text variant="subheader-2">{title}</Text>
                    <Text variant="body-2">{contact.name || ''}</Text>
                </div>

                <div className={b('contact-email')}>
                    <TooltipCopy copyText={contact.email || ''}>
                        <Icon className={b('icon')} data={Envelope} />
                        <Text variant="body-1">{contact.email || ''}</Text>
                    </TooltipCopy>
                </div>

                <div className={b('contact-number')}>
                    <TooltipCopy copyText={contact.phone || ''}>
                        <Icon className={b('icon')} data={Copy} />
                        <Text variant="body-1">{contact.phone || ''}</Text>
                    </TooltipCopy>

                    {showSocialButton ? (
                        <Button
                            type="button"
                            size="m"
                            className={b('social-button')}
                            aria-label="Открыть чат поддержки"
                            onClick={() => openTalkMeSupport()}
                        >
                            <Icon data={Comments} size={16} />
                        </Button>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export const ManagerInfo: React.FC = () => {
    const {
        state: {clientDetails}
    } = useContext(AppContext);

    const mop = clientDetails?.mop;
    const mrk = clientDetails?.mrk;
    const rop = clientDetails?.rop;

    const hasMop = hasContactData(mop);
    const hasMrk = hasContactData(mrk);
    const hasRop = hasContactData(rop);

    if (!hasMop && !hasMrk && !hasRop) {
        return null;
    }

    return (
        <div className={b()}>
            <Text variant="header-1">Менеджеры</Text>
            {hasMop && mop ? (
                <div className={b('manager')}>
                    <ManagerContactBlock contact={mop} title="Ваш менеджер:" avatarAlt="Менеджер" />
                </div>
            ) : null}
            {hasMrk && mrk ? (
                <div className={b('manager')}>
                    <ManagerContactBlock
                        contact={mrk}
                        title="Специалист клиентской поддержки:"
                        avatarAlt="Специалист клиентской поддержки"
                        showSocialButton
                    />
                </div>
            ) : null}
            {hasRop && rop ? (
                <div className={b('manager')}>
                    <ManagerContactBlock
                        contact={rop}
                        title="Руководитель отдела продаж:"
                        avatarAlt="Руководитель отдела продаж"
                    />
                </div>
            ) : null}
        </div>
    );
};
